/**
 * SkyLutSet — owns the four Hillaire LUTs and their dirty-flag regeneration.
 *
 * Two regeneration tiers (Hillaire §3, "what depends on what"):
 *
 *   Tier A — Transmittance + Multi-Scatter
 *     Depend ONLY on the atmosphere config. Re-baked when the config key
 *     changes. Rarely (only when the planet / scattering coefficients
 *     change), so these are the expensive-but-infrequent bakes.
 *
 *   Tier B — Sky-View + Aerial-Perspective
 *     Depend on Tier A PLUS sun direction and camera altitude. Re-baked when
 *     the sun moves beyond `sunCosThreshold` (in cosine of sun-zenith) or the
 *     camera altitude moves beyond `altitudeThresholdKm`. NOT every frame.
 *
 * `update()` is called each frame by SkyRuntimeSystem with the current sun
 * direction + camera altitude; it returns which tiers (if any) re-baked so
 * the caller can trigger a PMREM env re-bake only when the sky actually
 * changed.
 *
 * Aerial-perspective baking is gated behind `enableAerialPerspective` (off by
 * default — its compositing is deferred, see render-sky.md).
 */

import type { AtmosphereConfig } from '../AtmosphereConfig.js';
import { atmosphereConfigKey } from '../AtmosphereConfig.js';
import { Logger } from '../../../core/Logger.js';
import {
  bakeTransmittanceLut, type TransmittanceLutResult,
} from './TransmittanceLut.js';
import {
  bakeMultiScatterLut, type MultiScatterLutResult,
} from './MultiScatterLut.js';
import {
  bakeSkyViewLut, type SkyViewLutResult,
} from './SkyViewLut.js';
import {
  bakeAerialPerspectiveLut, type AerialPerspectiveLutResult,
} from './AerialPerspectiveLut.js';

export interface SkyLutSetOptions {
  atmosphere: AtmosphereConfig;
  /** Sun zenith cosine change that triggers a Tier-B re-bake. Default 0.01. */
  sunCosThreshold?: number;
  /** Camera-altitude change (km) that triggers a Tier-B re-bake. Default 0.05. */
  altitudeThresholdKm?: number;
  /** Sun illuminance multiplier baked into sky-view radiance. Default 20. */
  sunIlluminance?: number;
  /** Bake the aerial-perspective volume too (compositing deferred). Default false. */
  enableAerialPerspective?: boolean;
}

export interface SkyLutUpdateResult {
  /** Transmittance + multi-scatter re-baked this update. */
  tierA: boolean;
  /** Sky-view (+ optional AP) re-baked this update. */
  tierB: boolean;
}

export class SkyLutSet {
  private _cfg: AtmosphereConfig;
  private _cfgKey: string;
  private readonly _sunCosThreshold: number;
  private readonly _altThreshold: number;
  private _sunIlluminance: number;
  private readonly _enableAP: boolean;

  private _transmittance: TransmittanceLutResult | null = null;
  private _multiScatter: MultiScatterLutResult | null = null;
  private _skyView: SkyViewLutResult | null = null;
  private _aerial: AerialPerspectiveLutResult | null = null;

  // Last Tier-B inputs for threshold checks.
  private _lastSunCos = Number.NaN;
  private _lastAltKm = Number.NaN;
  private _disposed = false;

  constructor(opts: SkyLutSetOptions) {
    this._cfg = opts.atmosphere;
    this._cfgKey = atmosphereConfigKey(opts.atmosphere);
    this._sunCosThreshold = opts.sunCosThreshold ?? 0.01;
    this._altThreshold = opts.altitudeThresholdKm ?? 0.05;
    this._sunIlluminance = opts.sunIlluminance ?? 20;
    this._enableAP = opts.enableAerialPerspective ?? false;
  }

  get transmittance(): TransmittanceLutResult | null { return this._transmittance; }
  get multiScatter(): MultiScatterLutResult | null { return this._multiScatter; }
  get skyView(): SkyViewLutResult | null { return this._skyView; }
  get aerial(): AerialPerspectiveLutResult | null { return this._aerial; }
  get sunIlluminance(): number { return this._sunIlluminance; }
  get aerialPerspectiveEnabled(): boolean { return this._enableAP; }

  setSunIlluminance(v: number): void { this._sunIlluminance = v; }

  /** Push a new atmosphere config; forces a Tier-A (+B) re-bake next update. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._cfg = cfg;
    this._cfgKey = atmosphereConfigKey(cfg);
  }

  /**
   * Re-bake whichever tiers are dirty.
   *
   * @param cosSunZenith  current sun-zenith cosine in the camera's up-frame
   * @param viewHeightKm  current camera radius from planet centre (km)
   * @param force         force both tiers (e.g. first frame after teleport)
   */
  update(cosSunZenith: number, viewHeightKm: number, force = false): SkyLutUpdateResult {
    if (this._disposed) return { tierA: false, tierB: false };

    let tierA = false;
    let tierB = false;

    // ── Tier A: config-only LUTs ──
    // Dirty when never baked OR the config key baked-against differs from the
    // current config key (setAtmosphere updates _cfgKey out of band).
    const needTierA = force
      || !this._transmittance
      || !this._multiScatter
      || this._lastBakedKey !== this._cfgKey;
    if (needTierA) {
      this._bakeTierA();
      tierA = true;
      // Tier A always invalidates Tier B.
      tierB = true;
    }

    // ── Tier B: sun + altitude dependent LUTs ──
    const sunMoved = Number.isNaN(this._lastSunCos)
      || Math.abs(cosSunZenith - this._lastSunCos) > this._sunCosThreshold;
    const altMoved = Number.isNaN(this._lastAltKm)
      || Math.abs(viewHeightKm - this._lastAltKm) > this._altThreshold;
    if (tierB || force || sunMoved || altMoved || !this._skyView) {
      this._bakeTierB(cosSunZenith, viewHeightKm);
      this._lastSunCos = cosSunZenith;
      this._lastAltKm = viewHeightKm;
      tierB = true;
    }

    return { tierA, tierB };
  }

  /** Config key that the current Tier-A LUTs were baked against. */
  private _lastBakedKey = '';

  private _bakeTierA(): void {
    this._transmittance?.texture.dispose();
    this._multiScatter?.texture.dispose();

    const t = bakeTransmittanceLut(this._cfg);
    this._transmittance = t;
    const ms = bakeMultiScatterLut({
      cfg: this._cfg,
      transmittanceData: t.data,
      transmittanceWidth: t.width,
      transmittanceHeight: t.height,
    });
    this._multiScatter = ms;
    this._lastBakedKey = this._cfgKey;
    Logger.info('SkyLUT', 'Baked transmittance (256×64) + multi-scatter (32×32)');
  }

  private _bakeTierB(cosSunZenith: number, viewHeightKm: number): void {
    if (!this._transmittance || !this._multiScatter) { this._bakeTierA(); }
    const tt = this._transmittance!;
    const mss = this._multiScatter!;

    const sv = bakeSkyViewLut({
      cfg: this._cfg,
      viewHeight: viewHeightKm,
      cosSunZenith,
      transmittanceData: tt.data,
      transmittanceWidth: tt.width,
      transmittanceHeight: tt.height,
      multiScatterData: mss.data,
      multiScatterSize: mss.width,
      sunIlluminance: this._sunIlluminance,
    });
    // Reuse the existing GPU texture if dimensions match so the background
    // node's texture reference stays valid across re-bakes (no swap → no
    // material recompile). The bake's freshly-allocated texture is discarded.
    if (this._skyView
      && this._skyView.width === sv.width && this._skyView.height === sv.height) {
      (this._skyView.texture.image.data as Float32Array).set(sv.data);
      this._skyView.texture.needsUpdate = true;
      this._skyView.data = sv.data;
      sv.texture.dispose();
    } else {
      this._skyView?.texture.dispose();
      this._skyView = sv;
    }

    if (this._enableAP) {
      const ap = bakeAerialPerspectiveLut({
        cfg: this._cfg,
        viewHeight: viewHeightKm,
        cosSunZenith,
        transmittanceData: tt.data,
        transmittanceWidth: tt.width,
        transmittanceHeight: tt.height,
        multiScatterData: mss.data,
        multiScatterSize: mss.width,
        sunIlluminance: this._sunIlluminance,
      });
      if (this._aerial && this._aerial.size === ap.size) {
        (this._aerial.texture.image.data as Float32Array).set(ap.data);
        this._aerial.texture.needsUpdate = true;
        this._aerial.data = ap.data;
        ap.texture.dispose();
      } else {
        this._aerial?.texture.dispose();
        this._aerial = ap;
      }
    }
  }

  dispose(): void {
    this._disposed = true;
    this._transmittance?.texture.dispose();
    this._multiScatter?.texture.dispose();
    this._skyView?.texture.dispose();
    this._aerial?.texture.dispose();
    this._transmittance = null;
    this._multiScatter = null;
    this._skyView = null;
    this._aerial = null;
  }
}
